import { useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { CountingGame, PictureMatchGame, ToneMarksGame } from '../games/Games';
import type { NumberItem, ToneSet, WordItem } from '../games/logic';
import { Empty, ErrorBox, Loading, NeedsReviewBadge, Notice, PageHeader } from '../components/ui';
import { LanguageNote } from '../components/LanguagePicker';
import { type FamilyLanguage, useLanguageCatalogue } from '../lib/languages';
import { useFamilyTerms } from '../lib/terms';
import type { Child } from './DashboardPage';

interface LessonSummary {
  id: string;
  title: string;
  english_title: string | null;
  brief: string | null;
  track: string;
  level: number;
  game_type: string;
  review_status: string;
  state: 'ready' | 'in_review' | 'planned';
  item_count: number;
}
interface Readiness {
  planned: number;
  written: number;
  approved: number;
  awaiting_review: number;
}
interface Lesson extends LessonSummary {
  content: { items?: WordItem[]; sets?: ToneSet[]; numbers?: NumberItem[]; objectives?: string[] };
}

const PLAYABLE = ['picture_match', 'tone_marks', 'counting'];
const ICON: Record<string, string> = { picture_match: '🖼️', tone_marks: '🎵', counting: '🐚', vocab: '📚', phrases: '💬', story: '📖', oriki_project: '🪘' };

export function PlayPage() {
  const { user, childMode } = useAuth();
  const isParent = user?.role === 'parent' && !childMode;
  const track = user?.role === 'teen' ? 'teen' : user?.role === 'child' ? 'kids' : undefined;
  // The family's own language, not Yorùbá for everyone. Every listed language has
  // the full thirty-lesson plan; what differs is how much of it is written.
  const { primary } = useFamilyTerms();
  const lang = primary?.code ?? 'yo';
  const lessons = useApi<{ lessons: LessonSummary[]; language: string; readiness: Readiness | null }>(
    `/lessons?language=${encodeURIComponent(lang)}${track ? `&track=${track}` : ''}`,
  );
  const family = useApi<{ children: Child[] }>(isParent ? '/family' : null);
  // Games exist for Yorùbá only. If this family's home language is something else, say so here
  // rather than letting an all-Yorùbá page imply their language is covered.
  const homeLanguages = useApi<{ languages: FamilyLanguage[] }>('/family/languages');
  const { byCode } = useLanguageCatalogue();
  const awaitingLessons = (homeLanguages.data?.languages ?? []).filter((l) => l.status !== 'lessons_live');
  const [active, setActive] = useState<Lesson | null>(null);
  const [childId, setChildId] = useState<string>('');
  const act = useAction();

  const open = async (id: string) => {
    const r = await act.run(() => api<{ lesson: Lesson }>(`/lessons/${id}`));
    if (r) setActive(r.lesson);
  };

  const finish = async (score: number) => {
    if (!active) return;
    if (isParent && !childId) {
      act.setNotice(`Score ${score}%. Choose a child above to save cowries.`);
      return;
    }
    const r = await act.run(() => api<{ cowriesEarned: number; totalCowries: number }>(`/lessons/${active.id}/progress`, { body: { score, childProfileId: isParent ? childId : undefined } }));
    if (r) act.setNotice(`+${r.cowriesEarned} cowries! 🐚 Total: ${r.totalCowries}`);
  };

  if (active) {
    const c = active.content;
    return (
      <>
        <PageHeader title={<span className="yo">{active.title}</span>} actions={<button className="btn ghost small" onClick={() => { setActive(null); act.setNotice(null); }}>← All games</button>} />
        {active.review_status !== 'approved' && (
          <p>
            <NeedsReviewBadge /> <span className="small muted">Starter content — a native speaker is still checking it.</span>
          </p>
        )}
        <div className="card">
          {active.game_type === 'picture_match' && c.items?.length ? <PictureMatchGame items={c.items} onFinish={finish} /> : null}
          {active.game_type === 'tone_marks' && c.sets?.length ? <ToneMarksGame sets={c.sets} onFinish={finish} /> : null}
          {active.game_type === 'counting' && c.numbers?.length ? <CountingGame numbers={c.numbers} onFinish={finish} /> : null}
          {!PLAYABLE.includes(active.game_type) || !(c.items?.length || c.sets?.length || c.numbers?.length) ? (
            <Empty>This lesson is still being written by our native-speaker team. Check back soon!</Empty>
          ) : null}
        </div>
        <Notice text={act.notice} />
        <ErrorBox error={act.error} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Play & Learn" termKey="page.play" lead="Short, gentle games. No timers, no leaderboards — just cowries to collect." />
      {lessons.data?.readiness && <Readiness readiness={lessons.data.readiness} language={byCode.get(lessons.data.language)?.name ?? lessons.data.language} />}
      {awaitingLessons.map((l) => (
        <div key={l.code} style={{ marginBottom: 16 }}>
          {/* Asking for a language to be written is a family decision, not a
              child's. In child mode the note still explains where the language
              stands; the button that speaks for the household does not appear. */}
          <LanguageNote language={byCode.get(l.code) ?? l} showRequest={isParent} />
        </div>
      ))}
      {isParent && (
        <div className="card row" style={{ marginBottom: 16 }}>
          <label htmlFor="who">Playing as</label>
          <select id="who" value={childId} onChange={(e) => setChildId(e.target.value)}>
            <option value="">(just trying it out)</option>
            {family.data?.children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
          <span className="small muted">Tip: use “Play as …” on the family home to hand the device to a child safely.</span>
        </div>
      )}
      {lessons.loading && <Loading />}
      <ErrorBox error={lessons.error ?? act.error} />
      <div className="grid grid-3">
        {lessons.data?.lessons.map((l) => {
          const playable = PLAYABLE.includes(l.game_type) && l.item_count > 0;
          // Three honest states: ready to play, written and being checked by a
          // speaker, or planned and waiting for someone who speaks the language.
          const label = l.state === 'planned' ? 'not written yet' : playable ? 'play' : 'being checked';
          return (
            <button
              key={l.id}
              className="card stack"
              style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', opacity: playable ? 1 : 0.62 }}
              onClick={() => open(l.id)}
              aria-label={`${l.title} — ${label}`}
            >
              <span style={{ fontSize: '2rem' }} aria-hidden="true">{ICON[l.game_type] ?? '📚'}</span>
              <strong className="yo">{l.title}</strong>
              {l.state === 'planned' && l.brief && <span className="muted small">{l.brief}</span>}
              <span className="row small">
                <span className="badge">{l.track}</span>
                <span className="badge">level {l.level}</span>
                {playable ? <span className="badge ok">play</span> : <span className="badge">{label}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}


/**
 * How far this language has come, stated as counts a person can check.
 * A plan everyone can see beats an empty page that says nothing.
 */
function Readiness({ readiness, language }: { readiness: { planned: number; written: number; approved: number; awaiting_review: number }; language: string }) {
  const { planned, written, approved, awaiting_review } = readiness;
  const pct = planned ? Math.round((approved / planned) * 100) : 0;
  return (
    <section className="card stack" style={{ marginBottom: 16 }}>
      <div className="spread">
        <h2 style={{ margin: 0 }}>{language}</h2>
        <span className="muted small">{pct}% of the plan approved</span>
      </div>
      <div className="readiness-bar" role="img" aria-label={`${approved} of ${planned} lessons approved`}>
        <i className="done" style={{ width: `${planned ? (approved / planned) * 100 : 0}%` }} />
        <i className="review" style={{ width: `${planned ? (awaiting_review / planned) * 100 : 0}%` }} />
      </div>
      <span className="muted small">
        <strong>{planned}</strong> lessons planned · <strong>{written}</strong> written · <strong>{approved}</strong> approved by a native speaker
        {approved === 0 && written > 0 && ' — these are starter lessons and a speaker is still checking every word.'}
        {written === 0 && ' — nobody has written these yet. If you speak this language, you can.'}
      </span>
    </section>
  );
}
